import { MDBRadio, MDBBtn, MDBIcon, MDBInput, MDBCheckbox } from 'mdb-react-ui-kit';
import React, { useEffect, useState } from "react";
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { updateWaiverSettings } from "../services/apiServices";
import PageError from './PageError';
import { useAnalytics } from '../hooks/useAnalytics';

const DEFAULT_VERIFY_EMAIL_TEMPLATE = "<p>Hi <strong>{{name}}</strong>,</p><p>You submitted a waiver for <strong>{{businessName}}</strong>.</p><p>Please verify your email to complete your registration.</p><p>enter this code on the verification screen: </p><p><strong>                                                {{otp}}</strong> </p><p>\t                                    (Valid for 24 hours)</p><p>Didn’t submit this? You can safely ignore this email.</p>";

function WaiverSettings(props) {
    const { trackButtonClick } = useAnalytics();
    const [apiError, setApiError] = useState(false);
    const [showVerifyTemplatePreview, setShowVerifyTemplatePreview] = useState(false);
    const [businessForm, setBusinessForm] = useState({
        uniqueNumber: "",
        emergencycontact: 2,
        bloodgroup: 2,
        address: 2,
        phone: 1,
        email: 1,
        pincode: 2,
        governmentidproof: 2,
        consultingcontact: 2,
        title: `Digital Waiver Form`,
        checkboxes: [],
        governmentIds: [], // New field for government IDs
        requireEmailVerification: false,
        verifyEmailTemplateContent: DEFAULT_VERIFY_EMAIL_TEMPLATE,
        agreement: `Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et
dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris nisi ut
aliquip ex ea commodo consequat. Duis aute irure dolor in reprehenderit in voluptate velit esse cillum
dolore eu fugiat nulla pariatur. Excepteur sint occaecat cupidatat non proident, sunt in culpa qui officia
deserunt mollit anim id est laborum`
    });

    useEffect(() => {
        if (props.data) {
            const formattedData = { ...props.data };

            // Handle existing checkboxcontent data (old format)
            if (props.data.checkboxcontent && typeof props.data.checkboxcontent === 'string' && !props.data.checkboxes) {
                formattedData.checkboxes = [{
                    id: Date.now(),
                    text: props.data.checkboxcontent,
                    required: true
                }];
            }
            // Handle new format with checkboxes array
            else if (props.data.checkboxes && Array.isArray(props.data.checkboxes) && props.data.checkboxes.length > 0) {
                formattedData.checkboxes = props.data.checkboxes;
            }
            // Default checkboxes if none exist
            else if (!props.data.checkboxes || props.data.checkboxes.length === 0) {
                formattedData.checkboxes = [{
                    id: Date.now(),
                    text: `By checking this box, I acknowledge that I have read, understood, and agree to the terms and conditions outlined in the waiver form. I accept full responsibility for my participation, or for the participation of the minor I am legally responsible for, in climbing activities at Crag Studio, and recognize the inherent risks involved.`,
                    required: true
                }];
            }

            // Handle government IDs - default values if none exist
            if (props.data.governmentIds && Array.isArray(props.data.governmentIds)) {
                formattedData.governmentIds = props.data.governmentIds;
            } else if (!props.data.governmentIds || props.data.governmentIds.length === 0) {
                // Default government IDs
                formattedData.governmentIds = [
                    { id: Date.now() + 1, name: "Aadhar Card" },
                    { id: Date.now() + 2, name: "PAN Card" },
                    { id: Date.now() + 3, name: "Driving License" }
                ];
            }

            formattedData.requireEmailVerification = !!props.data.requireEmailVerification;
            if (formattedData.requireEmailVerification) {
                formattedData.email = 1;
            }
            formattedData.verifyEmailTemplateContent = props.data.verifyEmailTemplateContent || DEFAULT_VERIFY_EMAIL_TEMPLATE;
            setBusinessForm(formattedData);
        }
    }, [props.data]);

    useEffect(() => {
        if (businessForm.requireEmailVerification && businessForm.email !== 1) {
            setBusinessForm((prev) => ({ ...prev, email: 1 }));
        }
    }, [businessForm.requireEmailVerification, businessForm.email]);

    const renderVerifyTemplatePreview = (template) => {
        const source = (template || DEFAULT_VERIFY_EMAIL_TEMPLATE);
        const replaced = source
            .replace(/{{\s*name\s*}}/gi, "Member Name")
            .replace(/{{\s*businessName\s*}}/gi, "Demo")
            .replace(/{{\s*otp\s*}}/gi, "405145");
        const looksLikeHtml = /<[^>]+>/.test(replaced);
        if (looksLikeHtml) {
            return replaced;
        }
        return replaced.replace(/\r?\n/g, "<br/>");
    };

    function updateForm(value) {
        return setBusinessForm((prev) => {
            return { ...prev, ...value };
        });
    }

    // Add new checkbox
    const addCheckbox = () => {
        const newCheckbox = {
            id: Date.now(),
            text: "",
            required: true
        };
        updateForm({
            checkboxes: [...businessForm.checkboxes, newCheckbox]
        });
    };

    // Remove checkbox
    const removeCheckbox = (id) => {
        const updatedCheckboxes = businessForm.checkboxes.filter(
            checkbox => checkbox.id !== id
        );
        updateForm({ checkboxes: updatedCheckboxes });
    };

    // Update checkbox text
    const updateCheckboxText = (id, text) => {
        const updatedCheckboxes = businessForm.checkboxes.map(checkbox =>
            checkbox.id === id ? { ...checkbox, text } : checkbox
        );
        updateForm({ checkboxes: updatedCheckboxes });
    };

    // Toggle checkbox required status
    const toggleCheckboxRequired = (id) => {
        const updatedCheckboxes = businessForm.checkboxes.map(checkbox =>
            checkbox.id === id ? { ...checkbox, required: !checkbox.required } : checkbox
        );
        updateForm({ checkboxes: updatedCheckboxes });
    };

    // Government ID functions
    const addGovernmentId = () => {
        const newId = {
            id: Date.now(),
            name: ""
        };
        updateForm({
            governmentIds: [...businessForm.governmentIds, newId]
        });
    };

    const removeGovernmentId = (id) => {
        const updatedIds = businessForm.governmentIds.filter(
            govId => govId.id !== id
        );
        updateForm({ governmentIds: updatedIds });
    };

    const updateGovernmentIdName = (id, name) => {
        const updatedIds = businessForm.governmentIds.map(govId =>
            govId.id === id ? { ...govId, name } : govId
        );
        updateForm({ governmentIds: updatedIds });
    };

    function DigitalWaverShow() {
        setBusinessForm(props.data);
        props.toggleShow();
    }

    async function onSaveButton() {
        // Validate at least one checkbox has content
        const hasEmptyCheckbox = businessForm.checkboxes.some(cb => !cb.text.trim());
        if (hasEmptyCheckbox) {
            props.onbacktoStaff("Please fill in all checkbox texts or remove empty checkboxes");
            return;
        }

        if (businessForm.checkboxes.length === 0) {
            props.onbacktoStaff("Please add at least one checkbox");
            return;
        }

        // Validate government IDs - ensure no empty names
        const hasEmptyGovId = businessForm.governmentIds.some(govId => !govId.name.trim());
        if (hasEmptyGovId) {
            props.onbacktoStaff("Please fill in all government ID names or remove empty entries");
            return;
        }

        // Prepare data for API
        const checkboxContents = businessForm.checkboxes.map(cb => cb.text).join('|||');

        const updates = {
            gymid: localStorage.getItem("loggedUserInfo") ? JSON.parse(localStorage.getItem("loggedUserInfo")).gymid : "",
            title: businessForm.title,
            checkboxcontent: checkboxContents,
            checkboxes: businessForm.checkboxes,
            governmentIds: businessForm.governmentIds, // New field
            emergencycontact: businessForm.emergencycontact,
            bloodgroup: businessForm.bloodgroup,
            address: businessForm.address,
            phone: businessForm.phone,
            email: businessForm.email,
            agreement: businessForm.agreement,
            consultingcontact: businessForm.consultingcontact,
            pincode: businessForm.pincode,
            governmentidproof: businessForm.governmentidproof,
            requireEmailVerification: !!businessForm.requireEmailVerification,
            verifyEmailTemplateContent: businessForm.verifyEmailTemplateContent || DEFAULT_VERIFY_EMAIL_TEMPLATE,
        };

        try {
            const saverep = await updateWaiverSettings(updates, localStorage.getItem("loggedUserInfo") ? JSON.parse(localStorage.getItem("loggedUserInfo")).gymid : "");
            if (saverep === "false") {
                setApiError(true);
            } else {
                const recinfo = await saverep.json();
                console.log(recinfo);
                props.onSave(recinfo);
            }
            await trackButtonClick("WaiverSettings", "Updated_Digital_Waiver", "staff", {});
            props.onbacktoStaff("Waiver settings updated successfully");
        } catch (error) {
            console.error("Error updating waiver settings:", error);
            props.onbacktoStaff("Error updating waiver settings");
        }
    }

    const handleReload = () => {
        window.location.reload();
    }

    // Quill modules configuration
    const modules = {
        toolbar: [
            [{ 'header': [1, 2, 3, false] }],
            ['bold', 'italic', 'underline', 'strike'],
            [{ 'list': 'ordered' }, { 'list': 'bullet' }],
            ['link', 'clean']
        ],
    };

    const formats = [
        'header', 'bold', 'italic', 'underline', 'strike',
        'list', 'bullet', 'link'
    ];

    return (
        <React.Fragment>
            {apiError ? <PageError handleReload={handleReload} /> :
                <React.Fragment>
                    {/* Title Section */}
                    <div className="d-flex align-items-center mb-3">
                        <label className="fw-bold me-2" style={{ minWidth: '80px' }}>Title:</label>
                        <MDBInput
                            type="text"
                            className="w-100"
                            value={businessForm.title}
                            onChange={(e) => updateForm({ title: e.target.value })}
                        />
                    </div>

                    <div className="card shadow-0 border mt-3 p-3" style={{ backgroundColor: '#f8f9fa' }}>
                        <label className="fw-bold d-block mb-2" style={{ fontSize: '1.05rem' }}>
                            <MDBIcon fas icon="envelope-open-text" className="me-2" />
                            Waiver email verification
                        </label>
                        <MDBCheckbox
                            id="requireEmailVerification"
                            label="Require email verification before completing waiver submission"
                            checked={!!businessForm.requireEmailVerification}
                            onChange={() => {
                                const next = !businessForm.requireEmailVerification;
                                if (next) {
                                    updateForm({ requireEmailVerification: true, email: 1 });
                                } else {
                                    updateForm({ requireEmailVerification: false });
                                }
                            }}
                        />
                        <div className="text-muted ps-4 mt-1" style={{ fontSize: '12px' }}>
                            <MDBIcon fas icon="info-circle" className="me-1" />
                            When off, submitting the digital waiver creates the member and waiver immediately. When on, submitters must confirm their email verfication before records are created.
                        </div>
                    </div>

                    <div className="card shadow-0 border mt-3 p-3" style={{ backgroundColor: '#f8f9fa' }}>
                        <label className="fw-bold d-block mb-2" style={{ fontSize: '1.05rem' }}>
                            <MDBIcon fas icon="envelope" className="me-2" />
                            Edit verify email template
                        </label>
                        <ReactQuill
                            theme="snow"
                            value={businessForm.verifyEmailTemplateContent || ""}
                            onChange={(value) => updateForm({ verifyEmailTemplateContent: value })}
                            modules={modules}
                            formats={formats}
                            style={{ minHeight: '180px', marginBottom: '40px' }}
                        />
                        <div className="text-muted mt-2" style={{ fontSize: '12px' }}>
                            Use placeholders: <code>{'{{name}}'}</code>, <code>{'{{businessName}}'}</code>, <code>{'{{otp}}'}</code>.
                            The verify button is always shown at the end of the email.
                        </div>
                        <div className="mt-3">
                            <MDBBtn
                                size="sm"
                                style={{ boxShadow: 'none' }}
                                onClick={() => setShowVerifyTemplatePreview(true)}
                            >
                                <MDBIcon fas icon="eye" className="me-2" />
                                Preview Email Template
                            </MDBBtn>
                        </div>
                    </div>

                    {showVerifyTemplatePreview && (
                        <div
                            style={{
                                position: 'fixed',
                                inset: 0,
                                backgroundColor: 'rgba(0,0,0,0.45)',
                                zIndex: 1050,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                padding: '16px',
                            }}
                            onClick={() => setShowVerifyTemplatePreview(false)}
                        >
                            <div
                                className="bg-white rounded p-3 shadow"
                                style={{ width: '100%', maxWidth: '760px', maxHeight: '85vh', overflowY: 'auto' }}
                                onClick={(e) => e.stopPropagation()}
                            >
                                <div className="d-flex justify-content-between align-items-center mb-3">
                                    <h6 className="mb-0">Verify Email Preview</h6>
                                    <MDBBtn
                                        size="sm"
                                        color="light"
                                        style={{ boxShadow: 'none' }}
                                        onClick={() => setShowVerifyTemplatePreview(false)}
                                    >
                                        <MDBIcon fas icon="times" />
                                    </MDBBtn>
                                </div>
                                <div className="border rounded p-3 bg-white">
                                    <div
                                        style={{ lineHeight: 1.6 }}
                                        dangerouslySetInnerHTML={{ __html: renderVerifyTemplatePreview(businessForm.verifyEmailTemplateContent) }}
                                    />
                                    <div className="mt-3">
                                        <MDBBtn size="sm" disabled style={{ cursor: 'default' }}>
                                            Agree & Verify
                                        </MDBBtn>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    <div className="d-flex flex-column Consentcheckbox mt-2">
                        {/* Agreement Text Section */}
                        <label className="fw-bold mt-3 mb-2">Agreement Text</label>
                        <ReactQuill
                            theme="snow"
                            value={businessForm.agreement}
                            onChange={(value) => updateForm({ agreement: value })}
                            modules={modules}
                            formats={formats}
                            style={{ height: '200px', marginBottom: '50px' }}
                        />

                        {/* Multiple Checkboxes Section */}
                        <div className='mt-4 d-flex flex-column'>
                            <div className="d-flex justify-content-between align-items-center mb-3">
                                <label className="fw-bold" style={{ fontSize: '1.1rem' }}>
                                    <MDBIcon fas icon="check-square" className="me-2" />
                                    Checkboxes
                                </label>
                                <MDBBtn
                                    size="sm"
                                    onClick={addCheckbox}
                                    className="btn-sm"
                                    style={{ backgroundColor: '#4caf50', boxShadow: 'none' }}
                                >
                                    <MDBIcon fas icon="plus" className="me-1" /> Add Checkbox
                                </MDBBtn>
                            </div>

                            {businessForm.checkboxes && businessForm.checkboxes.map((checkbox, index) => (
                                <div key={checkbox.id} className="card  shadow-0 mb-2 p-3 py-2" style={{ backgroundColor: '#f8f9fa', border: '1px solid #e0e0e0' }}>
                                    <div className="d-flex justify-content-between align-items-center mb-1">
                                        <label className="fw-bold" style={{ color: '#1976d2', fontSize: '15px' }}>
                                            <MDBIcon fas icon="check" className="me-2" />
                                            Checkbox {index + 1}
                                        </label>

                                        <MDBBtn
                                            size="sm"
                                            onClick={() => removeCheckbox(checkbox.id)}
                                            className="btn-sm rounded-pill custom-outline-danger"
                                            style={{ boxShadow: 'none' }}
                                        >
                                            <MDBIcon fas icon="trash" className="me-1" /> Remove
                                        </MDBBtn>
                                    </div>

                                    <div className="mb-2">
                                        <label className="mb-1" style={{ fontSize: '13px' }}>
                                            Checkbox Text
                                        </label>
                                        <textarea
                                            rows="3"
                                            className="form-control"
                                            value={checkbox.text}
                                            onChange={(e) => updateCheckboxText(checkbox.id, e.target.value)}
                                            placeholder="Enter checkbox text (e.g., I agree to the terms and conditions...)"
                                            style={{ resize: 'vertical' }}
                                        />
                                    </div>

                                    <div style={{ lineHeight: '8px' }}>
                                        <MDBCheckbox
                                            id={`required_${checkbox.id}`}
                                            label="This field is required"
                                            checked={checkbox.required}
                                            onChange={() => toggleCheckboxRequired(checkbox.id)}
                                            className=""
                                        />
                                        <span className="text-muted ps-4" style={{ fontSize: '12px' }}>
                                            <MDBIcon fas icon="info-circle" className="me-1" />
                                            If checked, user must check this box to submit the form
                                        </span>
                                    </div>
                                </div>
                            ))}

                            {businessForm.checkboxes && businessForm.checkboxes.length === 0 && (
                                <div className="text-center text-muted p-4 border rounded" style={{ backgroundColor: '#fafafa' }}>
                                    <MDBIcon fas icon="info-circle" className="me-2" />
                                    No checkboxes added. Click "Add Checkbox" to create one.
                                </div>
                            )}

                            {businessForm.checkboxes && businessForm.checkboxes.length > 0 && (
                                <div className="ps-2 text-muted" style={{ fontSize: '12px' }}>
                                    <MDBIcon fas icon="lightbulb" className="me-1" />
                                    You can add multiple checkboxes for different agreements, terms, or consent items
                                </div>
                            )}
                        </div>

                        {/* Government ID Section */}
                        <div className='mt-5 d-flex flex-column'>
                            <div className="d-flex justify-content-between align-items-center mb-2">
                                <label className="fw-bold" style={{ fontSize: '1.1rem' }}>
                                    <MDBIcon fas icon="id-card" className="me-2" />
                                    Government ID Options
                                </label>
                                <MDBBtn
                                    size="sm"
                                    onClick={addGovernmentId}
                                    className="btn-sm"
                                    style={{ backgroundColor: '#2196f3', boxShadow: 'none' }}
                                >
                                    <MDBIcon fas icon="plus" className="me-1" /> Add ID Type
                                </MDBBtn>
                            </div>


                            <div className="alert alert-warning mb-3 d-flex align-items-center p-2" style={{ fontSize: '13px' }}>
                                <MDBIcon fas icon="info-circle" className="me-2" />
                                Configure which government ID types members can select from. Members will choose ONE ID type from this list.
                            </div>
                            {businessForm.governmentIds && businessForm.governmentIds.map((govId, index) => (
                                <div key={govId.id} className="card shadow-0 mb-2 p-3 py-2" style={{ backgroundColor: '#f8f9fa', border: '1px solid #e0e0e0' }}>
                                    <div className="d-flex justify-content-between align-items-start">
                                        <div className="flex-grow-1">
                                            <div className='d-flex align-items-center'>
                                                <label className="fw-bold w-15" style={{ color: '#1976d2', fontSize: '15px' }}>
                                                    ID Type {index + 1}
                                                </label>
                                                <div className='me-3 flex-1'>
                                                    <MDBInput
                                                        type="text"
                                                        className='bg-white'
                                                        value={govId.name}
                                                        onChange={(e) => updateGovernmentIdName(govId.id, e.target.value)}
                                                        placeholder="e.g., Aadhar Card, PAN Card, Driving License, Passport, Voter ID"
                                                    />
                                                </div>
                                            </div>
                                            <div className="mt-1 text-muted" style={{ fontSize: '12px', paddingLeft: '87px' }}>
                                                <b className='text-dark'> Example :</b>  Aadhar Card", "PAN Card", "Driving License", "Voter ID", "Passport"
                                            </div>
                                        </div>

                                        <MDBBtn
                                            size="sm"
                                            onClick={() => removeGovernmentId(govId.id)}
                                            className="btn-sm rounded-pill custom-outline-danger"
                                            style={{ boxShadow: 'none' }}
                                        >
                                            <MDBIcon fas icon="trash" className="me-1" /> Remove
                                        </MDBBtn>

                                    </div>
                                </div>
                            ))}

                            {businessForm.governmentIds && businessForm.governmentIds.length === 0 && (
                                <div className="text-center text-muted p-4 border rounded" style={{ backgroundColor: '#fafafa' }}>
                                    <MDBIcon fas icon="info-circle" className="me-2" />
                                    No government ID types added. Click "Add ID Type" to configure.
                                </div>
                            )}

                            {businessForm.governmentIds && businessForm.governmentIds.length > 0 && (
                                <div className="mt-2 text-muted" style={{ fontSize: '12px' }}>
                                    <MDBIcon fas icon="check-circle" className="me-1" style={{ color: '#4caf50' }} />
                                    Members will see these options in a dropdown and must select ONE ID type
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Optional Fields Section */}
                    <div className="d-flex flex-column mt-4">
                        <label className="fw-bold mb-3" style={{ fontSize: '1.1rem' }}>
                            <MDBIcon fas icon="user-edit" className="me-2" />
                            Optional Fields Configuration
                        </label>
                        <div className="p-2 border rounded">
                            <div className="mb-3 pb-2 border-bottom">
                                <div className="d-flex align-items-center justify-content-between flex-wrap">
                                    <label className="fw-bold" style={{ minWidth: '150px' }}>Email</label>
                                    <div className="d-flex gap-3 flex-wrap align-items-center">
                                        <MDBRadio
                                            name='emailRadio'
                                            label='Mandatory'
                                            inline
                                            checked={businessForm.email === 1}
                                            onChange={() => updateForm({ email: 1 })}
                                        />
                                        <MDBRadio
                                            name='emailRadio'
                                            label='Optional'
                                            inline
                                            disabled={!!businessForm.requireEmailVerification}
                                            checked={businessForm.email === 2}
                                            onChange={() => updateForm({ email: 2 })}
                                        />
                                        <MDBRadio
                                            name='emailRadio'
                                            label='Exclude'
                                            inline
                                            disabled={!!businessForm.requireEmailVerification}
                                            checked={businessForm.email === 3}
                                            onChange={() => updateForm({ email: 3 })}
                                        />
                                    </div>
                                </div>
                            </div>

                            <div className="mb-3 pb-2 border-bottom">
                                <div className="d-flex align-items-center justify-content-between flex-wrap">
                                    <label className="fw-bold" style={{ minWidth: '150px' }}>Phone</label>
                                    <div className="d-flex gap-3">
                                        <MDBRadio
                                            name='phoneRadio'
                                            label='Mandatory'
                                            inline
                                            checked={businessForm.phone === 1}
                                            onChange={() => updateForm({ phone: 1 })}
                                        />
                                        <MDBRadio
                                            name='phoneRadio'
                                            label='Optional'
                                            inline
                                            checked={businessForm.phone === 2}
                                            onChange={() => updateForm({ phone: 2 })}
                                        />
                                        <MDBRadio
                                            name='phoneRadio'
                                            label='Exclude'
                                            inline
                                            checked={businessForm.phone === 3}
                                            onChange={() => updateForm({ phone: 3 })}
                                        />
                                    </div>
                                </div>
                            </div>
                            <div className="mb-3 pb-2 border-bottom">
                                <div className="d-flex align-items-center justify-content-between flex-wrap">
                                    <label className="fw-bold" style={{ minWidth: '150px' }}>Government Proof</label>
                                    <div className="d-flex gap-3">
                                        <MDBRadio
                                            name='governmentRadio'
                                            label='Mandatory'
                                            inline
                                            checked={businessForm.governmentidproof === 1}
                                            onChange={() => updateForm({ governmentidproof: 1 })}
                                        />
                                        <MDBRadio
                                            name='governmentRadio'
                                            label='Optional'
                                            inline
                                            checked={businessForm.governmentidproof === 2}
                                            onChange={() => updateForm({ governmentidproof: 2 })}
                                        />
                                        <MDBRadio
                                            name='governmentRadio'
                                            label='Exclude'
                                            inline
                                            checked={businessForm.governmentidproof === 3}
                                            onChange={() => updateForm({ governmentidproof: 3 })}
                                        />
                                    </div>
                                </div>
                            </div>


                            <div className="mb-3 pb-2 border-bottom">
                                <div className="d-flex align-items-center justify-content-between flex-wrap">
                                    <label className="fw-bold" style={{ minWidth: '150px' }}>Emergency Contact</label>
                                    <div className="d-flex gap-3">
                                        <MDBRadio
                                            name='emergencyRadio'
                                            label='Mandatory'
                                            inline
                                            checked={businessForm.emergencycontact === 1}
                                            onChange={() => updateForm({ emergencycontact: 1 })}
                                        />
                                        <MDBRadio
                                            name='emergencyRadio'
                                            label='Optional'
                                            inline
                                            checked={businessForm.emergencycontact === 2}
                                            onChange={() => updateForm({ emergencycontact: 2 })}
                                        />
                                        <MDBRadio
                                            name='emergencyRadio'
                                            label='Exclude'
                                            inline
                                            checked={businessForm.emergencycontact === 3}
                                            onChange={() => updateForm({ emergencycontact: 3 })}
                                        />
                                    </div>
                                </div>
                            </div>

                            <div className="mb-3 pb-2 border-bottom">
                                <div className="d-flex align-items-center justify-content-between flex-wrap">
                                    <label className="fw-bold" style={{ minWidth: '150px' }}>Guardian Contact</label>
                                    <div className="d-flex gap-3">
                                        <MDBRadio
                                            name='guardianRadio'
                                            label='Mandatory'
                                            inline
                                            checked={businessForm.consultingcontact === 1}
                                            onChange={() => updateForm({ consultingcontact: 1 })}
                                        />
                                        <MDBRadio
                                            name='guardianRadio'
                                            label='Optional'
                                            inline
                                            checked={businessForm.consultingcontact === 2}
                                            onChange={() => updateForm({ consultingcontact: 2 })}
                                        />
                                        <MDBRadio
                                            name='guardianRadio'
                                            label='Exclude'
                                            inline
                                            checked={businessForm.consultingcontact === 3}
                                            onChange={() => updateForm({ consultingcontact: 3 })}
                                        />
                                    </div>
                                </div>
                            </div>

                            <div className="mb-3 pb-2 border-bottom">
                                <div className="d-flex align-items-center justify-content-between flex-wrap">
                                    <label className="fw-bold" style={{ minWidth: '150px' }}>Blood Group</label>
                                    <div className="d-flex gap-3">
                                        <MDBRadio
                                            name='bloodRadio'
                                            label='Mandatory'
                                            inline
                                            checked={businessForm.bloodgroup === 1}
                                            onChange={() => updateForm({ bloodgroup: 1 })}
                                        />
                                        <MDBRadio
                                            name='bloodRadio'
                                            label='Optional'
                                            inline
                                            checked={businessForm.bloodgroup === 2}
                                            onChange={() => updateForm({ bloodgroup: 2 })}
                                        />
                                        <MDBRadio
                                            name='bloodRadio'
                                            label='Exclude'
                                            inline
                                            checked={businessForm.bloodgroup === 3}
                                            onChange={() => updateForm({ bloodgroup: 3 })}
                                        />
                                    </div>
                                </div>
                            </div>

                            <div className="mb-3 pb-2 border-bottom">
                                <div className="d-flex align-items-center justify-content-between flex-wrap">
                                    <label className="fw-bold" style={{ minWidth: '150px' }}>Address</label>
                                    <div className="d-flex gap-3">
                                        <MDBRadio
                                            name='addressRadio'
                                            label='Mandatory'
                                            inline
                                            checked={businessForm.address === 1}
                                            onChange={() => updateForm({ address: 1 })}
                                        />
                                        <MDBRadio
                                            name='addressRadio'
                                            label='Optional'
                                            inline
                                            checked={businessForm.address === 2}
                                            onChange={() => updateForm({ address: 2 })}
                                        />
                                        <MDBRadio
                                            name='addressRadio'
                                            label='Exclude'
                                            inline
                                            checked={businessForm.address === 3}
                                            onChange={() => updateForm({ address: 3 })}
                                        />
                                    </div>
                                </div>
                            </div>

                            <div className="mb-3 pb-2">
                                <div className="d-flex align-items-center justify-content-between flex-wrap">
                                    <label className="fw-bold" style={{ minWidth: '150px' }}>Pin Code</label>
                                    <div className="d-flex gap-3">
                                        <MDBRadio
                                            name='pincodeRadio'
                                            label='Mandatory'
                                            inline
                                            checked={businessForm.pincode === 1}
                                            onChange={() => updateForm({ pincode: 1 })}
                                        />
                                        <MDBRadio
                                            name='pincodeRadio'
                                            label='Optional'
                                            inline
                                            checked={businessForm.pincode === 2}
                                            onChange={() => updateForm({ pincode: 2 })}
                                        />
                                        <MDBRadio
                                            name='pincodeRadio'
                                            label='Exclude'
                                            inline
                                            checked={businessForm.pincode === 3}
                                            onChange={() => updateForm({ pincode: 3 })}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Modal Footer Buttons */}
                    <div className="modal-footer pb-0 mt-4 pt-3" style={{ borderTop: '1px solid #dee2e6' }}>
                        <button
                            className="ripple ripple-surface btn btn-secondary px-4"
                            onClick={DigitalWaverShow}
                            style={{ boxShadow: 'none' }}
                        >
                            Cancel
                        </button>
                        <button
                            className="ripple ripple-surface btn btn-primary px-4"
                            onClick={onSaveButton}
                            style={{ boxShadow: 'none' }}
                        >
                            Save Changes
                        </button>
                    </div>
                </React.Fragment>}
        </React.Fragment>
    )
}

export default WaiverSettings;